import Link from "next/link";
import type { ReactNode } from "react";

/*
 * Kacheln der Intranet-Startseite V3 (Musterkatalog M-112, Design-Guide `heute-kachel.md` und `werkzeug-kachel.md`):
 * - `HeuteKachel`: EINE Zahl bzw. Zeile mit Link (Heute-Leiste), keine Liste (r113). Quelle: `feedback-and-states/
 *   feedback-and-states-sub-actions-engagement/feedback-and-states-cat-action-cards-and-shortcut-links/simple-icon-shortcut-card-grid-with-descriptions`.
 * - `WerkzeugKachel`: Schnellzugriff-Link als Kachel mit Symbol und Name (Werkzeugkasten). Quelle:
 *   `.../template-shortcut-grid-with-large-soft-icons` (Farbflächen des Blocks durch Theme-Token ersetzt).
 * Die Bausteine kennen keine Fachregeln und prüfen keine Rechte: WELCHE Kachel es gibt, entscheidet der Aufrufer (r109/r110).
 * Ziele außerhalb des Portals (`extern`) öffnen im neuen Fenster mit `rel="noopener noreferrer"`.
 */

const KACHEL = "group flex items-center gap-x-3 min-h-16 p-3 rounded-xl bg-layer border border-layer-line shadow-2xs hover:shadow-md focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus transition";
// KI-Kachel (R-D): dieselbe Kette mit `ai-gated ai-mark` davor, ausgeschrieben (Klassen nie zusammensetzen).
const KACHEL_KI = "ai-gated ai-mark group flex items-center gap-x-3 min-h-16 p-3 rounded-xl bg-layer border border-layer-line shadow-2xs hover:shadow-md focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus transition";
const SYMBOL_RUHIG = "shrink-0 inline-flex justify-center items-center size-11 rounded-full bg-muted text-foreground";
const SYMBOL_AUFMERKSAM = "shrink-0 inline-flex justify-center items-center size-11 rounded-full bg-primary text-primary-foreground";

export interface HeuteKachelProps {
  /** Stabile Kennung (Test-Anker). */
  id: string;
  /** Kurzes Wort über der Zeile („Aufgaben“). */
  titel: string;
  /** Die Zahl bzw. Zeile („3 Aufgaben fällig“); eine Zeile, wird bei Überlänge gekürzt. */
  zeile: string;
  href: string;
  /** Lucide-Symbol des Aufrufers (R-F), wird als dekorativ markiert. */
  icon: ReactNode;
  /** Hinweischarakter (z. B. Aufgaben fällig): Symbolfläche in Primärfarbe. Die Aussage steht im Text, nie nur in der Farbe. */
  aufmerksam?: boolean;
  /** KI-Kachel (R-D): `ai-gated ai-mark`; der Aufrufer reicht sie bei KI aus nicht herein. */
  ki?: boolean;
}

export function HeuteKachel({ id, titel, zeile, href, icon, aufmerksam = false, ki = false }: HeuteKachelProps) {
  return (
    <>
      {/* Preline: feedback-and-states/feedback-and-states-sub-actions-engagement/feedback-and-states-cat-action-cards-and-shortcut-links/simple-icon-shortcut-card-grid-with-descriptions */}
      <Link href={href} className={ki ? KACHEL_KI : KACHEL} data-heute-kachel={id} data-ki={ki ? "gated" : undefined}>
        <span className={aufmerksam ? SYMBOL_AUFMERKSAM : SYMBOL_RUHIG} aria-hidden="true">
          {icon}
        </span>
        <span className="min-w-0 grow">
          <span className="block text-xs text-muted-foreground-1">{titel}</span>
          <span className="block truncate text-sm font-semibold text-foreground group-hover:text-primary-hover">{zeile}</span>
        </span>
      </Link>
    </>
  );
}

/** Zeile der Heute-Kacheln: eine Spalte mobil, zwei ab `sm`, vier ab `xl`. */
export function HeuteKachelRaster({ children, ariaLabel = "Heute" }: { children: ReactNode; ariaLabel?: string }) {
  return (
    <section aria-label={ariaLabel} className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
      {children}
    </section>
  );
}

export interface WerkzeugKachelProps {
  id: string;
  label: string;
  href: string;
  icon: ReactNode;
  /** Ziel außerhalb des Portals: neues Fenster. */
  extern?: boolean;
}

const WERKZEUG = "group min-w-0 flex flex-col items-center justify-center gap-y-2 min-h-24 p-3 text-center rounded-xl bg-layer border border-layer-line shadow-2xs hover:shadow-md focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus transition";

export function WerkzeugKachel({ id, label, href, icon, extern = false }: WerkzeugKachelProps) {
  const inhalt = (
    <>
      <span className="inline-flex justify-center items-center size-11 rounded-full bg-muted text-foreground" aria-hidden="true">
        {icon}
      </span>
      <span title={label} className="max-w-full min-w-0 line-clamp-2 break-words text-sm font-medium text-foreground group-hover:text-primary-hover">{label}</span>
      {extern && <span className="sr-only">(öffnet in neuem Fenster)</span>}
    </>
  );
  return (
    <>
      {/* Preline: feedback-and-states/feedback-and-states-sub-actions-engagement/feedback-and-states-cat-action-cards-and-shortcut-links/template-shortcut-grid-with-large-soft-icons */}
      {extern ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className={WERKZEUG} data-werkzeug-kachel={id}>
          {inhalt}
        </a>
      ) : (
        <Link href={href} className={WERKZEUG} data-werkzeug-kachel={id}>
          {inhalt}
        </Link>
      )}
    </>
  );
}

/** Raster der Werkzeug-Kacheln: zwei Spalten mobil, drei ab `sm`, vier ab `lg`, sechs ab `xl`. */
export function WerkzeugKasten({ children, ariaLabel = "Werkzeugkasten" }: { children: ReactNode; ariaLabel?: string }) {
  return (
    <section aria-label={ariaLabel} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
      {children}
    </section>
  );
}
